import { CaretDownIcon } from '@radix-ui/react-icons';
import { Button, DropdownMenu } from '@radix-ui/themes';
import {
  CircleHalfIcon,
  CloudFogIcon,
  CloudLightningIcon,
  CloudRainIcon,
  MagicWandIcon,
  SnowflakeIcon,
  SparkleIcon,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { useSceneEffectsSync } from '../../../hooks/useSceneEffectsSync';
import { useTranslation, type MessageKey } from '../../../i18n';
import type { SceneEffectType } from '../../../types/sceneEffects';

const EFFECTS: { id: SceneEffectType; titleKey: MessageKey; icon: Icon }[] = [
  { id: 'fog', titleKey: 'toolbar.tools.fog', icon: CloudFogIcon },
  { id: 'rain', titleKey: 'toolbar.tools.rain', icon: CloudRainIcon },
  { id: 'snow', titleKey: 'toolbar.tools.snow', icon: SnowflakeIcon },
  { id: 'lightning', titleKey: 'toolbar.tools.lightning', icon: CloudLightningIcon },
  { id: 'particles', titleKey: 'toolbar.tools.particles', icon: SparkleIcon },
  { id: 'vignette', titleKey: 'toolbar.tools.vignette', icon: CircleHalfIcon },
];

export function EffectsTools() {
  const { t } = useTranslation();
  const { sceneEffects, toggleEffect, clearEffects } = useSceneEffectsSync();
  const enabledEffects = sceneEffects.filter((effect) => effect.isEnabled);
  const hasActiveEffects = enabledEffects.length > 0;

  return (
    <div className="effects-tools">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <Button
            variant="soft"
            radius="none"
            size="3"
            className={`effects-tools-trigger ${hasActiveEffects ? 'active' : ''}`}
            aria-label={t('toolbar.sceneEffects')}
          >
            <MagicWandIcon aria-hidden="true" weight={hasActiveEffects ? 'fill' : 'regular'} />
            <CaretDownIcon aria-hidden="true" className="effects-tools-chevron" />
            {hasActiveEffects && (
              <span
                className="effects-tools-count"
                aria-label={t('toolbar.activeEffects', { count: enabledEffects.length })}
              >
                {enabledEffects.length}
              </span>
            )}
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content variant="soft" size="2" align="start" sideOffset={4}>
          <DropdownMenu.Label>{t('toolbar.sceneEffectsLabel')}</DropdownMenu.Label>
          {EFFECTS.map((effect) => {
            const IconComponent = effect.icon;
            const instance = sceneEffects.find((item) => item.effectType === effect.id);
            const isActive = instance?.isEnabled ?? false;
            return (
              <DropdownMenu.CheckboxItem
                key={effect.id}
                checked={isActive}
                onCheckedChange={() => void toggleEffect(effect.id)}
                className={isActive ? 'effects-tools-item-active' : undefined}
              >
                <IconComponent aria-hidden="true" weight={isActive ? 'fill' : 'regular'} />
                {t(effect.titleKey)}
              </DropdownMenu.CheckboxItem>
            );
          })}
          {hasActiveEffects && (
            <>
              <DropdownMenu.Separator />
              <DropdownMenu.Item color="gray" onSelect={() => void clearEffects()}>
                {t('toolbar.clearAllEffects')}
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </div>
  );
}
